import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { ComplexTransfer } from 'api/Transaction/Types/ComplexTransfer'
import { RawTransactionFilterKey } from 'api/Transaction/Types/RawTransactionDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { initialState } from 'store/grid'
import {
  fetchDirectionType,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty } from 'utils/is-empty'
import { FilterLogic } from 'utils/QueryHelpers'
import { TransactionStatus } from 'utils/transaction/status.index'

import SelectTransaction, { TransactionFilterType } from '../../SelectTransaction'
import { AdjustmentWalletForm } from './AdjustmentWalletForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto
  setClient(): void
}

export const AdjustmentWalletPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [isSelectTransactionModalOpen, setSelectTransactionModal] = useState<boolean>(false)
  const [linkedTransaction, selectedLinkedTransaction] = useState<TransactionDto | null>(null)
  const [confirmationData, setConfirmationData] = useState<ComplexTransfer>()
  const [isConfirmationModalOpen, setConfirmationModal] = useState<boolean>(false)
  const [transaction] = confirmationData?.transactions || []

  const setToast = useContext(ToastContext)

  const transactionTypes = useAppSelector((state) => state.transactions.types)
  const directionTypes = useAppSelector((state) => state.transactions.directionType)

  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [wallet, setWalletId] = useState<string>('')

  const dispatch = useAppDispatch()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client])

  useEffect(() => {
    dispatch(fetchTransactionTypeAction(walletsApiClient, { tags: 'WalletAdjustments' }))
    if (isEmpty(directionTypes)) dispatch(fetchDirectionType(walletsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: wallets, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (preSelectedWallet) {
      setWallet(preSelectedWallet.id)
    }
  }, [wallets, preSelectedWallet]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: ComplexTransfer) => {
    setConfirmationData(values)
    setConfirmationModal(true)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const response = await walletsApiClient.createListOfSingleWalletTransfer(confirmationData)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: `Transaction has been submitted.`,
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: response.id,
          }),
        })
        if (linkedTransaction) {
          await walletsApiClient.linkTransaction(response.id, linkedTransaction.id, 10)
        }
        forceRefresh()
        setConfirmationModal(false)
      } catch (e) {
        console.error(e)
        setConfirmationModal(false)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModal(false)
  }

  const setWallet = async (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    if (walletDto) {
      setWalletId(walletDto.id)
      setCurrency(walletDto.currency.id)
    }
  }

  const getSelectedTransaction = (item: TransactionDto) => {
    selectedLinkedTransaction(item)
    setSelectTransactionModal(false)
  }

  const onSetClient = () => {
    setClient()
    selectedLinkedTransaction(null)
  }

  const selectWalletName = () => {
    const [transactionItem] = confirmationData?.transactions || []
    return (wallets || [])?.find((x) => x.id === transactionItem?.walletId)?.name
  }

  const selectTransactionTypeName = () => {
    const transactionTypeId = confirmationData?.transactionTypeId?.toString()
    return transactionTypes?.find((x) => x?.id === Number.parseInt(transactionTypeId!, 10))?.name
  }

  const selectDirectionTypeName = () => {
    const [transactionItem] = confirmationData?.transactions || []
    return directionTypes?.find(
      (x) => x?.id === Number.parseInt(transactionItem?.directionTypeId, 10)
    )?.name
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to do an adjustment for the wallet {selectWalletName()}. The
                parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>Wallet:</strong> {selectWalletName()}
                </div>
                <div id='field'>
                  <strong>Transaction Type:</strong> {selectTransactionTypeName()}
                </div>
                <div id='field'>
                  <strong>Direction Type:</strong> {selectDirectionTypeName()}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {transaction?.amount} {currency}
                </div>
              </div>
            </div>
          }
        />
      )}
      {isSelectTransactionModalOpen && wallet && (
        <Modal
          width={1300}
          visible={isSelectTransactionModalOpen}
          onClose={() => setSelectTransactionModal(false)}
        >
          <SelectTransaction
            walletId={wallet}
            filterBy={TransactionFilterType.Wallet}
            getTransaction={getSelectedTransaction}
            query={createWalletTransactionsQuery()}
          />
        </Modal>
      )}
      <AdjustmentWalletForm
        directionTypes={directionTypes}
        transactionTypes={transactionTypes}
        walletId={wallet}
        setWallet={setWallet}
        currency={currency}
        setCurrency={setCurrency}
        client={client}
        setClient={onSetClient}
        linkedTransaction={linkedTransaction}
        setLinkedTransaction={() => setSelectTransactionModal(true)}
        wallets={wallets || []}
        onSubmit={onSubmit}
      />
    </React.Fragment>
  )
}

const createWalletTransactionsQuery = () => {
  const initialGridState = initialState()

  return {
    ...initialGridState.filter,
    logic: initialGridState.filter.logic,
    defaultFilters: {
      logic: FilterLogic.AND,
      filters: [
        {
          field: RawTransactionFilterKey.state,
          operator: '==',
          value: TransactionStatus.Completed,
        },
        {
          logic: FilterLogic.OR,
          filters: [
            {
              field: RawTransactionFilterKey.type,
              operator: '==',
              value: TransactionType.WalletDeposit,
            },
            {
              field: RawTransactionFilterKey.type,
              operator: '==',
              value: TransactionType.WalletWithdrawal,
            },
          ],
        },
      ],
    },
  }
}
